import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

// ==================== Types ====================

export interface Department {
  id: string
  name: string
  code: string
  parentId: string | null
  managerId: string | null
  sortOrder: number | null
  isActive: boolean | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface DepartmentTreeNode extends Department {
  children: DepartmentTreeNode[]
  employeeCount?: number
}

export interface DepartmentWithCounts extends Department {
  employeeCount: number
  childCount: number
}

export interface FlatDepartment {
  id: string
  name: string
  code: string
  parentId: string | null
}

export interface CreateDepartmentInput {
  name: string
  code: string
  parentId?: string
  managerId?: string
  sortOrder?: number
}

export interface UpdateDepartmentInput {
  name?: string
  code?: string
  parentId?: string | null
  managerId?: string | null
  sortOrder?: number
  isActive?: boolean
}

// ==================== Query Keys ====================

export const departmentKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  list: () => [...departmentKeys.lists(), 'tree'] as const,
  flat: () => [...departmentKeys.lists(), 'flat'] as const,
  details: () => [...departmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentKeys.details(), id] as const,
}

// ==================== Hooks ====================

/**
 * 获取部门树形列表
 */
export function useDepartmentTree() {
  return useQuery({
    queryKey: departmentKeys.list(),
    queryFn: () => get<DepartmentTreeNode[]>('/hr/departments'),
  })
}

/**
 * 获取部门扁平列表（下拉选择用）
 */
export function useDepartments() {
  return useQuery({
    queryKey: departmentKeys.flat(),
    queryFn: () => get<FlatDepartment[]>('/hr/departments/flat'),
  })
}

/**
 * 获取单个部门详情
 */
export function useDepartment(id: string) {
  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: () => get<DepartmentWithCounts>(`/hr/departments/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建部门
 */
export function useCreateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDepartmentInput) => post<Department>('/hr/departments', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() })
    },
  })
}

/**
 * 更新部门
 */
export function useUpdateDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDepartmentInput }) =>
      patch<Department>(`/hr/departments/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() })
    },
  })
}

/**
 * 删除部门
 */
export function useDeleteDepartment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/hr/departments/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: departmentKeys.lists() })
    },
  })
}
